import React from 'react';
import { currentArchivePathAtom } from 'js/file_viewer/core/atoms/metadata';
import { baseFileAtom, rootFileMetadataAtom, rootPreviewKeyAtom, sharedLinkInfoAtom, } from 'js/file_viewer/core/atoms/metadata';
import { imageLocalDimensionsAtom } from 'js/file_viewer/core/atoms/ui/image';
import { filePreviewSessionIdAtom } from 'js/file_viewer/core/atoms/ui/logging';
import { useFileViewerAtomValue, usePreviewKeyAtomValue } from 'js/file_viewer/core/atoms/use_atom';
import { useRootPreviewMetadata as useRootPreviewMetadataInternal } from 'js/file_viewer/core/queries/preview_metadata';
import { useIsPasswordProtected, useSsrDocPageDimensions, } from 'js/file_viewer/core/queries/riviera_preview_types/utils/ssr_doc';
import { PreviewType } from 'js/file_viewer/core/types/preview_type';
import { useRootPreviewKey } from 'js/file_viewer/hooks/selectors';
import { useExtractedText } from 'js/file_viewer/hooks/useExtractedText';
/**
 * @returns {PreviewData}
 *   The metadata for the preview being displayed. These metadata are the core data required to view a file. When
 *   viewing a file nested within an archive file, this will return the metadata for the archive file, not the nested
 *   file.
 */
export const useRootPreviewMetadata = () => {
    return useRootPreviewMetadataInternal();
};
/**
 * @returns {PreviewType}
 *   The preview type for the root file being previewed. The "root" file means that nested archive files are not
 *   considered. For example, if the user navigates to a document within an archive file, this will still return
 *   PreviewType.Archive. This is different from useCurrentPreviewMetadata, which will return the nested file.
 */
export const useRootPreviewType = () => {
    const previewData = useRootPreviewMetadata();
    return previewData.previewType;
};
/**
 * @returns {ResolvedFileInfo}
 *  The file being previewed. This is the file that is being displayed in the preview. When viewing a file nested
 *  within an archive file, this will return the archive file, not the nested file.
 */
export const useRootFile = () => {
    var _a, _b;
    const rootPreviewData = useRootPreviewMetadata();
    const rootPreviewKey = useRootPreviewKey();
    const previewType = useRootPreviewType();
    const imageLocalDimensions = usePreviewKeyAtomValue(imageLocalDimensionsAtom, rootPreviewKey);
    // We should use the API provided dimensions if available, otherwise we can fallback to locally calculated ones
    // Some files like PSD don't have metadata extracted, so we need to rely on locally calculated dimensions
    const imageWidth = React.useMemo(() => {
        var _a;
        return ((_a = rootPreviewData === null || rootPreviewData === void 0 ? void 0 : rootPreviewData.dimensions) === null || _a === void 0 ? void 0 : _a.width) || (imageLocalDimensions === null || imageLocalDimensions === void 0 ? void 0 : imageLocalDimensions.width) || 0;
    }, [imageLocalDimensions === null || imageLocalDimensions === void 0 ? void 0 : imageLocalDimensions.width, (_a = rootPreviewData === null || rootPreviewData === void 0 ? void 0 : rootPreviewData.dimensions) === null || _a === void 0 ? void 0 : _a.width]);
    const imageHeight = React.useMemo(() => {
        var _a;
        return ((_a = rootPreviewData === null || rootPreviewData === void 0 ? void 0 : rootPreviewData.dimensions) === null || _a === void 0 ? void 0 : _a.height) || (imageLocalDimensions === null || imageLocalDimensions === void 0 ? void 0 : imageLocalDimensions.height) || 0;
    }, [imageLocalDimensions === null || imageLocalDimensions === void 0 ? void 0 : imageLocalDimensions.height, (_b = rootPreviewData === null || rootPreviewData === void 0 ? void 0 : rootPreviewData.dimensions) === null || _b === void 0 ? void 0 : _b.height]);
    const imageDimensions = React.useMemo(() => {
        return {
            width: imageWidth,
            height: imageHeight,
        };
    }, [imageWidth, imageHeight]);
    const baseFile = useFileViewerAtomValue(baseFileAtom);
    return React.useMemo(() => {
        if (!baseFile) {
            return null;
        }
        switch (previewType) {
            case PreviewType.Image:
                return {
                    ...baseFile,
                    dimensions: imageDimensions,
                };
            default:
                return baseFile;
        }
    }, [baseFile, previewType, imageDimensions]);
};
/**
 * @returns {InternalFileMetadata | undefined}
 *  The root FileMetadata info.
 *  It contains info such as file_name, file_path and file_collection_context
 */
export const useRootFileMetadata = () => {
    return useFileViewerAtomValue(rootFileMetadataAtom);
};
/**
 * @returns {SharedLinkInfo}
 *   The shared link information for the file being previewed. Undefined if the file is not a shared file.
 */
export const useSharedLinkInfo = () => {
    return useFileViewerAtomValue(sharedLinkInfoAtom);
};
/**
 * @returns {string[] | undefined}
 *   This returns the current path we are browsing inside the Archive file.
 *   Each element corresponds to a (sub)folder, except the last element can be a file.
 *   This is always defined if a file type is archive. If we are viewing the top level of the archive,
 *   the return value is [].
 *   It is not defined if we are viewing a top level file that is not an archive.
 */
export const useArchiveSubpath = () => {
    return useFileViewerAtomValue(currentArchivePathAtom);
};
/**
 * @returns {boolean}
 *  True if the current file is nested inside an archive file or if the user is viewing a subdirectory of an archive.
 *  Returns false if we are viewing the top level of an archive or a non-archive file.
 */
export const useIsNestedArchiveFile = () => {
    const archiveSubpath = useArchiveSubpath();
    return !!archiveSubpath && archiveSubpath.length > 0;
};
/**
 * @returns {boolean}
 * True if the root file is a password protected document. False if it is not password protected or not a document.
 */
export const useIsRootDocumentPasswordProtected = () => {
    var _a;
    const rootPreviewKey = useFileViewerAtomValue(rootPreviewKeyAtom);
    const rootPreviewMetadata = useRootPreviewMetadata();
    return (_a = useIsPasswordProtected(rootPreviewKey, rootPreviewMetadata)) !== null && _a !== void 0 ? _a : false;
};
/**
 * @returns {[number, number][] | undefined}
 * Returns an array of page dimensions of the form `[width, height]` for the
 * root document preview. Returns undefined if the root preview is not a
 * document or if the preview data is still loading.
 */
export const useRootDocumentPageDimensions = () => {
    const rootPreviewKey = useFileViewerAtomValue(rootPreviewKeyAtom);
    const rootPreviewMetadata = useRootPreviewMetadata();
    return useSsrDocPageDimensions(rootPreviewKey, rootPreviewMetadata);
};
/**
 * @returns {string}
 * Return the text content of a text preview
 */
export const useRootExtractedText = () => {
    const previewKey = useRootPreviewKey();
    return useExtractedText(previewKey);
};
/**
 * @returns {string}
 * Return the file preview session id of the current preview
 */
export const useFilePreviewSessionId = () => {
    return useFileViewerAtomValue(filePreviewSessionIdAtom);
};
//# sourceMappingURL=data.preenvesnextjs.map
